import React from 'react';
import { useTranslation } from '@app/core/i18n';

import AngleDown from '@ast/magma/components/icon/icons/AngleDown';

import { Button } from '@ast/magma/components/button';

import { uniqueId } from '@ast/magma/inner/utils/uniqueid';

import styles from './styles.pcss';

export type AccountsDropDownButtonBaseProps = {
  /**
 * Placeholder text for button,
 * before value were selected.
 * Also, will be used as heading for
 * dropdown list when its opened.
 */
  readonly buttonPlaceholder: string

  readonly children?: React.ReactElement | null

  /**
   * Label text above dropdown
   */
  readonly label?: string
};

type AccountsDropDownButtonProps = AccountsDropDownButtonBaseProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export const AccountsDropDownButton = React.forwardRef(
  (
    props: AccountsDropDownButtonProps,
    ref: React.Ref<HTMLButtonElement>,
  ) => {
    const { t } = useTranslation();
    const {
      buttonPlaceholder,
      children,
      label,
      value,
      ...buttonProps
    } = props;

    const descUid = React.useMemo(() => uniqueId('account-dropdown-button-desc'), []);

    const defaultButton = (
      <Button
        className={styles.dropDownButton}
        look="primary"
        type="button"
      >
        <div className={styles.placeholder}>
          <span className={styles.caption}>
            { buttonPlaceholder }
          </span>
          <AngleDown className={styles.icon} />
        </div>
      </Button>
    );

    const params = {
      'aria-describedby': descUid,
      ...buttonProps,
      extended: 'true',
      selected: undefined,
      tabIndex: 0,
      ref,
    };

    let renderedButton;

    if (children && React.Children.count(children) === 1) {
      renderedButton = React.cloneElement(children, params);
    } else {
      renderedButton = React.cloneElement(defaultButton, params);
    }

    return (
      <>
        { renderedButton }
        { label && (
          <span
            className={styles.aria}
            id={descUid}
          >
            {t(
              'accounts-dropdown.button.aria-description|Aria description for dropdown',
              '{{label}} dropdown.',
              { label },
            )}
          </span>
        )}
      </>
    );
  },
);
